import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Animated, AppState } from 'react-native';
import * as Font from 'expo-font';
import { Feather } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Navigation from './src/navigation';
import { STRIPE_PUBLISHABLE_KEY } from './src/lib/stripe';
import { ToastProvider } from './src/components/Toast';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { requestNotificationPermission, checkOverdueInvoices } from './src/lib/notifications';
import { reviveDriveDetectionIfNeeded } from './src/tasks/driveDetection';
import { supabase } from './src/lib/supabase';
import { ThemeProvider } from './src/contexts/ThemeContext';
import * as Updates from 'expo-updates';

function UpdateBanner() {
  const [ready, setReady] = useState(false);
  const [applying, setApplying] = useState(false);
  const slideAnim = React.useRef(new Animated.Value(-80)).current;
  const checking = useRef(false);

  async function checkForUpdate() {
    if (Platform.OS === 'web' || __DEV__ || checking.current || ready) return;
    checking.current = true;
    try {
      const result = await Updates.checkForUpdateAsync();
      if (!result.isAvailable) return;
      await Updates.fetchUpdateAsync();
      setReady(true);
      Animated.spring(slideAnim, { toValue: 0, useNativeDriver: true, tension: 80, friction: 12 }).start();
    } catch (_) {} finally {
      checking.current = false;
    }
  }

  useEffect(() => {
    // Delay startup check so it doesn't compete with app initialization
    const startupDelay = setTimeout(() => checkForUpdate(), 10000);
    // Also check when app comes back to foreground (no delay needed)
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        checkForUpdate();
        reviveDriveDetectionIfNeeded();
      }
    });
    return () => { clearTimeout(startupDelay); sub.remove(); };
  }, []);

  if (!ready) return null;

  return (
    <Animated.View style={[updateStyles.banner, { transform: [{ translateY: slideAnim }] }]}>
      <Text style={updateStyles.text}>✦ Update ready</Text>
      <TouchableOpacity
        style={[updateStyles.btn, applying && updateStyles.btnDim]}
        onPress={async () => {
          if (applying) return;
          setApplying(true);
          await Updates.reloadAsync();
        }}
        activeOpacity={0.8}
      >
        <Text style={updateStyles.btnText}>{applying ? 'Restarting…' : 'Restart now'}</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

const updateStyles = StyleSheet.create({
  banner: {
    position: 'absolute',
    bottom: 100,
    left: 20,
    right: 20,
    backgroundColor: '#1C2333',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(79,142,247,0.35)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 12,
    zIndex: 9999,
  },
  text: { fontSize: 14, fontWeight: '600', color: '#FFFFFF', letterSpacing: -0.2 },
  btn: {
    backgroundColor: '#4F8EF7',
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 14,
  },
  btnDim: { opacity: 0.6 },
  btnText: { fontSize: 13, fontWeight: '700', color: '#FFFFFF' },
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('App error boundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={errorStyles.container}>
          <Text style={errorStyles.title}>Something went wrong</Text>
          <Text style={errorStyles.message}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </Text>
          <TouchableOpacity
            style={errorStyles.btn}
            onPress={() => this.setState({ hasError: false, error: null })}
          >
            <Text style={errorStyles.btnText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

const errorStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0F1E',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: -0.5,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 32,
  },
  btn: {
    backgroundColor: '#4F8EF7',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 32,
  },
  btnText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

function AppContent() {
  useEffect(() => {
    Font.loadAsync(Feather.font).catch(() => {});
    // Defer non-critical startup work so it doesn't compete with initial render
    const t = setTimeout(() => {
      requestNotificationPermission();
      checkOverdueInvoices(supabase).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <ThemeProvider>
          <ToastProvider>
            <Navigation />
            <StatusBar style="auto" />
            <UpdateBanner />
          </ToastProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

export default function App() {
  if (Platform.OS === 'web') {
    return (
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    );
  }

  // Native: wrap with StripeProvider
  const { StripeProvider } = require('@stripe/stripe-react-native');
  return (
    <ErrorBoundary>
      <StripeProvider
        publishableKey={STRIPE_PUBLISHABLE_KEY}
        merchantIdentifier="merchant.com.slate.quotes"
        urlScheme="slate-quotes"
      >
        <AppContent />
      </StripeProvider>
    </ErrorBoundary>
  );
}
